import React, { useEffect, useMemo, useState } from "react";
import { useCatalog } from "../lib/catalog-context";
import {
  listPackages,
  listPortfolioFeatured,
  listTestimonialsFeatured,
} from "../lib/api";
import HeroCarousel from "../components/home/HeroCarousel";
import ServiceTilesSection from "../components/home/ServiceTilesSection";
import PackagesRail from "../components/home/PackagesRail";
import PortfolioRail from "../components/home/PortfolioRail";
import TestimonialsSection from "../components/home/TestimonialsSection";
import BigCtaBand from "../components/home/BigCtaBand";
import ErrorBanner from "../components/shared/ErrorBanner";

export default function Home() {
  const { services, banners, loading: catLoading, error: catError } = useCatalog();
  const [packages, setPackages] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [coversByService, setCoversByService] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [pkgs, port, test] = await Promise.all([
          listPackages({ limit: 6 }),
          listPortfolioFeatured({ limit: 6 }),
          listTestimonialsFeatured(),
        ]);
        if (cancelled) return;
        setPackages(pkgs);
        setPortfolio(port);
        setTestimonials(test);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  // Fetch one cover image per service (for service tiles)
  useEffect(() => {
    if (!services?.length) return undefined;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        services.map(async (svc) => {
          try {
            const imgs = await listPortfolioFeatured({ service_id: svc.id, limit: 1 });
            return [svc.id, imgs[0] || null];
          } catch {
            return [svc.id, null];
          }
        }),
      );
      if (!cancelled) setCoversByService(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [services]);

  const coversByPkgId = useMemo(() => {
    // Pick one portfolio image per service to use as package cover fallback
    const byService = {};
    portfolio.forEach((img) => {
      if (img.service_id && !byService[img.service_id]) byService[img.service_id] = img;
    });
    const out = {};
    packages.forEach((p) => {
      out[p.id] = byService[p.service_id] || portfolio[0] || null;
    });
    return out;
  }, [packages, portfolio]);

  return (
    <div data-testid="home-page">
      <HeroCarousel banners={banners} />

      {catError || error ? (
        <div className="mx-auto max-w-6xl px-5 pt-10 sm:px-6">
          <ErrorBanner onRetry={() => setRetryKey((k) => k + 1)} />
        </div>
      ) : null}

      <ServiceTilesSection
        services={services}
        coversById={coversByService}
        loading={catLoading}
      />
      <PackagesRail
        packages={packages}
        coversByPkgId={coversByPkgId}
        loading={loading}
      />
      <PortfolioRail images={portfolio} loading={loading} />
      <TestimonialsSection testimonials={testimonials} loading={loading} />
      <BigCtaBand />
    </div>
  );
}
